import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";

export interface DateDto {
  id: string;
  type: "FIELD" | "EVENT" | "EXTERNAL_CALENDAR";
  name: string;
  dateValue: string;
  status: "PAST" | "FUTURE";
  isComplete: boolean;
  isNotApplicable?: boolean; // For FIELD types - whether the date is marked as N/A
  projectId: string | null; // null for EXTERNAL_CALENDAR types
  projectName: string | null; // null for EXTERNAL_CALENDAR types
  participants: DateParticipantDto[];
  documentId?: string; // For FIELD types - the document this field belongs to
  documentName?: string; // For FIELD types - the document name
  documentStatus?: string; // For FIELD types - the document status (for navigation logic)
  documentViewAccess?: string; // For FIELD types - the document viewAccess (for navigation logic)
  metadata?: any;
}

export interface DateParticipantDto {
  id: string;
  name: string;
  email?: string;
  role?: string;
  avatarRef?: string;
}

export interface GroupedDatesResponse {
  grouped: true;
  data: { [projectId: string]: { projectName: string; dates: DateDto[] } };
}

export interface FlatDatesResponse {
  grouped: false;
  data: DateDto[];
}

export type DatesResponse = GroupedDatesResponse | FlatDatesResponse;

export interface DateQueryParams {
  dealId?: string;
  startDateTime?: string; // ISO string
  endDateTime?: string; // ISO string
  groupBy?: "deal";
  search?: string;
  sortByClosestTo?: string; // ISO string
}

export const datesApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "datesApi",
    baseUrl: "/api/dates",
  }),
  reducerPath: "datesApi",
  tagTypes: ["Dates"],
  endpoints: (builder) => ({
    getDates: builder.query<DatesResponse, DateQueryParams>({
      query: (args) => {
        const queryParams = new URLSearchParams();
        if (args.dealId) queryParams.append("dealId", args.dealId);
        if (args.startDateTime)
          queryParams.append("startDateTime", args.startDateTime);
        if (args.endDateTime)
          queryParams.append("endDateTime", args.endDateTime);
        if (args.groupBy) queryParams.append("groupBy", args.groupBy);
        if (args.search) queryParams.append("search", args.search);
        if (args.sortByClosestTo)
          queryParams.append("sortByClosestTo", args.sortByClosestTo);
        return `?${queryParams.toString()}`;
      },
      keepUnusedDataFor: 0,
      providesTags: [{ type: "Dates" }],
    }),
  }),
});

export const { useGetDatesQuery, useLazyGetDatesQuery } = datesApi;

// Utility functions for common date ranges
export const createDateRange = {
  currentWeek: (): Pick<DateQueryParams, "startDateTime" | "endDateTime"> => {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    return {
      startDateTime: startOfWeek.toISOString(),
      endDateTime: endOfWeek.toISOString(),
    };
  },

  currentMonth: (): Pick<DateQueryParams, "startDateTime" | "endDateTime"> => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(
      now.getFullYear(),
      now.getMonth() + 1,
      0,
      23,
      59,
      59,
      999
    );

    return {
      startDateTime: startOfMonth.toISOString(),
      endDateTime: endOfMonth.toISOString(),
    };
  },

  // NOTE: these ranges are used directly as RTK Query cache keys, so their
  // boundaries MUST be stable within a render pass. Returning `new Date()` at
  // millisecond precision produces a brand-new cache key on every single
  // render, which means a fresh network request every render — a
  // self-sustaining refetch loop. Both ends are snapped to day boundaries.
  next30Days: (): Pick<DateQueryParams, "startDateTime" | "endDateTime"> => {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    const thirtyDaysFromNow = new Date(startOfToday);
    thirtyDaysFromNow.setDate(startOfToday.getDate() + 30);
    thirtyDaysFromNow.setHours(23, 59, 59, 999);

    return {
      startDateTime: startOfToday.toISOString(),
      endDateTime: thirtyDaysFromNow.toISOString(),
    };
  },

  pastWeek: (): Pick<DateQueryParams, "startDateTime" | "endDateTime"> => {
    const endOfToday = new Date();
    endOfToday.setHours(23, 59, 59, 999);

    const weekAgo = new Date(endOfToday);
    weekAgo.setDate(endOfToday.getDate() - 7);
    weekAgo.setHours(0, 0, 0, 0);

    return {
      startDateTime: weekAgo.toISOString(),
      endDateTime: endOfToday.toISOString(),
    };
  },

  custom: (
    startDate: Date,
    endDate: Date
  ): Pick<DateQueryParams, "startDateTime" | "endDateTime"> => ({
    startDateTime: startDate.toISOString(),
    endDateTime: endDate.toISOString(),
  }),
};

/*
Usage examples:

// Basic usage - get all dates for a specific deal
const { data } = useGetDatesQuery({ dealId: 'deal-123' });

// Get dates for current month across all projects, grouped by deal
const { data } = useGetDatesQuery({
  ...createDateRange.currentMonth(),
  groupBy: 'deal'
});

// Get upcoming dates for next 30 days (great for dashboard)
const { data } = useGetDatesQuery({
  ...createDateRange.next30Days()
});

// Custom date range
const { data } = useGetDatesQuery({
  ...createDateRange.custom(new Date('2024-01-01'), new Date('2024-01-31')),
  dealId: 'deal-123'
});

// Response handling:
if (data?.grouped) {
  // data.data is { [projectId]: { projectName, dates } }
  Object.entries(data.data).forEach(([projectId, { projectName, dates }]) => {
    console.log(`${projectName}: ${dates.length} dates`);
  });
} else {
  // data.data is DateDto[]
  console.log(`${data.data.length} dates found`);
}
*/
